<template>
	<VDialogV3
	  id="assembly-card"
	  position="bottom"

	  :entity="localAssembly"

	  show-edit-button
	  :edit-button-disabled="localAssembly?.isShipped ? 'По этой сборке уже совершена отгрузка' : null"
	  :submit-disabled="stateEntity"
	  :submit-method="submit"
	  :errors="dialogErrors"
	  :context-menu="AssembliesService.contextMenu"

	  @edit-button:clicked="showRequisitesDialog"
	>
	  <template #badges>
      <VBadge v-if="localAssembly.isOfferBased === false" class="primary">Прямой</VBadge>
	  </template>

	  <header class="document-header">
      <dl>
        <dt>Грузоотправитель</dt>
        <dd>{{ localAssembly.sellerString }}</dd>
        <dt>Пункт отгрузки</dt>
        <dd>{{ localAssembly.sellerStoreString }}</dd>

        <hr>

        <dt>Дата готовности</dt>
        <dd>
        {{ localAssembly.assemblyDate ? $core.utils.date.format(localAssembly.assemblyDate) : 'Не задано' }}
        </dd>
        <dt>Груз</dt>
        <dd>{{ localAssembly.cargoUnitsString }}</dd>
        <dt>Доставка</dt>
        <dd>
        t° {{ localAssembly.storeTemperatureRange?.toString({ unit: '°C', negative: 'без температурных ограничений' }) }},
        {{ localAssembly.deliveryType?.name.toLowerCase() }}
        </dd>
      </dl>

      <dl>
        <dt>Грузополучатель</dt>
        <dd>{{ localAssembly.buyerString }}</dd>
        <dt>Пункт доставки</dt>
        <dd>{{ localAssembly.buyerStoreString }}</dd>

        <hr>

        <dt>Ответственный</dt>
        <dd>{{ localAssembly.responsible?.name ?? '--' }}</dd>
        <dt>Категория</dt>
        <dd>{{ localAssembly.orderCategory?.name ?? 'Не выбран' }}</dd>
        <template v-if="!$core.currentPartner?.isSeller">
        <dt>ЗТК</dt>
        <dd>
          <template v-if="localAssembly.transportOrder">
            <VLink @click="TransportOrdersService.showTransportOrderDialog(localAssembly.transportOrder.id)">
              № {{ localAssembly.transportOrder.id }}
            </VLink>
          </template>
          <template v-else>
            --
          </template>
        </dd>
        </template>
      </dl>
	  </header>

	  <VDataGridV2
      :data="localAssembly?.positions"
      layout="fill"
	  >
      <VDataGridColumnV2
        id="id"
        content-align="center"
        width="94px"
      >
        <template #label>
          <div>Арт</div>
          <div class="description">№</div>
        </template>
        <template #default="{ row }">
          <VLink
            class="ellipsis"
            :title="$core.utils.string.formatWithDelimiter($core.currentPartner?.isSeller ? row.orderPosition?.sellersCode : row.orderPosition?.buyersCode)"
            @click="OrdersService.showCodeInfoPopup(row.orderPosition, $event.currentTarget)"
          >
            <template v-if="$core.currentPartner?.isSeller">
            {{ row.orderPosition?.sellersCode ?? '--' }}
            </template>
            <template v-else>
            {{ row.orderPosition?.buyersCode ?? '--' }}
            </template>
          </VLink>
          <div class="description nowrap">
            {{ $core.utils.string.formatWithDelimiter(row.orderPosition.id) }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="name"
        sortable
        content-class="ellipsis"
        width="300px"
      >
        <template #label>
          <div>Товар</div>
          <div class="description">Производитель</div>
        </template>
        <template #default="{ row }">
          <div
            @mouseenter="$core.tooltip.show($event.currentTarget, row.orderPosition.name)"
            @mouseleave="$core.tooltip.hide()"
            class="ellipsis"
          >
            {{ row.orderPosition.name }}
          </div>
          <div
            v-if="isRawMaterialsAndPackage"
            class="description ellipsis"
          >
            {{ row.orderPosition.manufacturer.name }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        content-align="right"
        content-class="form"
        width="180px"
      >
        <template #label>
          <div class="qty">Кол-во</div>
        </template>
        <template #default="{ row, index }">
          <VInputNumberRusV2
            :ref="'qtyInput' + index"
            v-model="row.qty"
            :type="Qty"
            required
            autocomplete="off"
            :warning-method="(value) => validatePositionQty(value, row)"
            :unit="row.orderPosition.unit.name"
          >
            <template #helper-text>
              <template v-if="isPositionShippedEntirely(row)">
                <template v-if="row.qty.greaterThan(getPositionQtyReminder(row))">
                  <span class="error">
                    Превышен максимум на
                    {{ row.qty.minus(getPositionQtyReminder(row)).format() }}
                    {{ row.orderPosition.unit.name }}
                  </span>
                </template>
                <template v-else>
                  <span class="nowrap">
                    В работе {{ getPositionQtyReminder(row).minus(row.qty).format() }}
                    {{ row.orderPosition.unit.name }}
                  </span>
                </template>
              </template>
              <template v-else>
                <span class="nowrap">
                  В работе {{ getInWorkQty(row) }}
                  {{ row.orderPosition.unit.name }}
                </span>
              </template>
            </template>
          </VInputNumberRusV2>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="priceFullSeller"
        label="Цена полная"
        header-class="nowrap"
        content-align="right"
        content-class="nowrap"
        width="110px"
      >
        <template #default="{ row }">
          <template v-if="row.orderPosition.priceFullSeller">
            {{ row.orderPosition.priceFullSeller.format() }} {{ row.orderPosition.currency.symbol }}
          </template>
          <template v-else>--</template>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="storeTemperatureRange"
        label="Темп. режим"
        header-class="nowrap"
        content-align="left"
        content-class="nowrap"
        width="120px"
      >
        <template #default="{ row }">
          {{ row.orderPosition.storeTemperatureRange?.toString({ unit: '°C', negative: 'Нет' }) }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="isShipped"
        label="Отгружен"
        content-align="center"
        width="60px"
      >
        <template #default="{ row }">
          <template v-if="row.isShipped">
            <div class="nowrap">
              {{ row.qtyShipped.format() }}
              {{ row.orderPosition.unit.name }}
            </div>
            <div class="description">
              {{ row.isShippedEntirely ? 'Полностью' : 'Частично' }}
            </div>
          </template>
          <template v-else>
            Нет
          </template>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="desiredReceiptDate"
        label="Дата доставки"
        header-class="nowrap"
        content-align="center"
        content-class="nowrap"
        width="120px"
      >
        <template #default="{ row }">
          {{ $core.utils.date.format(row.orderPosition.desiredReceiptDate, 'DD|MM|yyyy') }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="plannedShipmentDate"
        label="Дата отгрузки"
        header-class="nowrap"
        content-align="center"
        content-class="nowrap"
        width="120px"
      >
        <template #default="{ row }">
          {{ $core.utils.date.format(row.orderPosition.plannedShipmentDate, 'DD|MM|yyyy') }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="isOrderNeededToBeClosed"
        content-align="left"
        content-class="form"
        width="120px"
      >
        <template #label>
          Закрыть заказ
          <VLink
            class="closed-status-link description"
            :disabled="localAssembly.isOfferBased === false && $core.currentPartner.isSeller === true"
            @click="localAssembly.closeAllOrderPositions()"
          >
            Установить для всех
          </VLink>
        </template>
        <template #default="{ row }">
          <VSwitch
            v-model="row.isOrderNeededToBeClosed"
            theme="slider"
            :disabled="isPositionShippedEntirely(row) || localAssembly.isOfferBased === false && $core.currentPartner.isSeller === true"
            checked-label="Закрыть"
            unchecked-label="Нет"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="isPositionShippedEntirely"
        content-class="form"
        width="70px"
      >
        <template #default="{ row, index }">
          <VButton
            material="delete"
            :disabled="isPositionShippedEntirely(row)"
            :title="isPositionShippedEntirely(row) ? 'Позиция отгружена' : null"
            @click="handleDeletePosition(row, index)"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridActionBarV2
        class="centered"
        :disabled="localAssembly?.isShipped ? 'По этой сборке уже совершена отгрузка — позиции редактировать нельзя' : null"
      >
        <VLink @click="showOrderPositionRegistryDialog" material="add">
          Добавить позицию
        </VLink>

        <VLink @click="showImportAssemblyPositionDialog" material="add">
          Импортировать
        </VLink>
      </VDataGridActionBarV2>
	  </VDataGridV2>

	  <hr>

	  <CargoUnitsGrid
      :cargo-units-owner="localAssembly"
      :editable="localAssembly.isShipped === false"
	  />

	</VDialogV3>
</template>

<script lang="ts">

import type OrderPosition     from '@/domains/Orders/OrderPosition';
import AssemblyPosition       from '@/domains/Assemblies/AssemblyPosition';
import Assembly               from '@/domains/Assemblies/Assembly';
import Qty                    from '@/core/types/Qty';
import AssembliesService      from '@/domains/Assemblies/AssembliesService';
import CriticalValidationError from '@/core/misc/CriticalValidationError';
import OrdersService          from '@/domains/Orders/OrdersService';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import CargoUnitsGrid         from '@/domains/CargoUnits/components/CargoUnitsGrid/CargoUnitsGrid.vue';


export default defineComponent({
  name: 'AssemblyCard',

  components: {
    CargoUnitsGrid,
  },

  props: {
    /** ID сборки — передаётся диалоговой системой (роутер/deep link). */
    id: {
      type:    Number,
      default: null,
    },

    /**
     * Позиции заказа, из которых создаётся новая сборка.
     * Приходят из реестра позиций — выделением или пунктом меню.
     */
    orderPositions: {
      type:    Array as PropType<OrderPosition[]>,
      default: null,
    },
  },

  setup() {
    return {
      TransportOrdersService,
      AssembliesService,
      OrdersService,

      // доступ к классу типа в шаблоне для :type="Qty"
      Qty,
    };
  },

  data() {
    return {
      localAssembly: null as Assembly | null,

      /**
       * Будет использовано в проверке, не изменял ли пользователь склад
       * поставщика, чтобы не запрашивать подтверждение при каждом редактировании
       */
      originalSellerStoreId: undefined as number | undefined,
    };
  },

  computed: {
    isValidPositions() {
      if (!Array.isArray(this.localAssembly?.positions)) { return false; }
      if (!this.localAssembly?.positions.length) { return false; }

      for (let i = 0; i < this.localAssembly.positions.length; i++) {
        // Количество должно быть задано и строго больше нуля.
        const qty = this.localAssembly.positions[i].qty;
        if (qty === null || !qty.isPositive()) { return false; }
      }
      return true;
    },

    hasPositions() {
      if (Array.isArray(this.localAssembly?.positions)) {
        return this.localAssembly!.positions.length > 0;
      }
      else {
        return false;
      }
    },

    isValidCargoUnits() {
      const hasCargoUnitFreePosition = this.localAssembly?.positions.some(assemblyPosition =>
        assemblyPosition.orderPosition?.buyersCode
        && Number(assemblyPosition.orderPosition?.buyersCode) === 110645,
      );
      /** Если есть освобожденный от паллетов товар, то паллеты не нужны соответственно */
      if (hasCargoUnitFreePosition) {
        return true;
      }

      return this.localAssembly?.cargoUnits.size > 0;
    },

    stateEntity() {
      if (!this.hasPositions) {
        return 'Добавьте позиции';
      }

      if (!this.isValidCargoUnits) {
        return 'Добавьте палеттоместо';
      }

      if (!this.isValidPositions) {
        return 'Заполните желаемое количество товара';
      }

      return null;
    },

    /**
     * Массив ошибок для плашки над футером VDialogV3.
     * Сейчас здесь живёт одна — про незаполненное состояние документа (stateEntity).
     * Без fieldRef, потому что ругаемся не на конкретное поле, а на состояние документа в целом.
     */
    dialogErrors() {
      if (this.stateEntity) {
        return [{ message: this.stateEntity }];
      }
      else {
        return [];
      }
    },

    isRawMaterialsAndPackage() {
      return this.localAssembly?.orderCategory?.id === 'rawMaterialsAndPackage';
    },
  },

  async mounted() {
    await this.init();

    if (this.localAssembly?.isExisting !== true) {
      this.showRequisitesDialog();
    }
  },

  methods: {

    async init() {
      if (this.id) {
        this.localAssembly = await AssembliesService.get(this.id!);
      }
      else if (this.$core.utils.isNotEmptyArray(this.orderPositions)) {
        // Сборка под выбранные позиции заказа
        this.localAssembly = Assembly.fromOrderPositions(this.orderPositions);
      }
      else {
        /** Сборки с нуля в интерфейсе нет: она всегда собирается из позиций заказа,
         *  которые задают ей шапку (продавца, покупателя, категорию, тип доставки и т. д.) */
        throw new CriticalValidationError(
          'AssemblyCard: сборку можно открыть только по id или создать из позиций заказа',
          { id: this.id, orderPositions: this.orderPositions },
        );
      }

      this.originalSellerStoreId = this.localAssembly?.sellerStore?.id;
    },

    showRequisitesDialog() {
      this.$core.dialogs.AssemblyRequisites.show(
        {
          assembly: this.localAssembly,
        },
        {
          updated: (assembly: Assembly) => {
            this.localAssembly = assembly;
          },
          close: () => {
            if (this.localAssembly?.isExisting !== true) {
              this.$core.dialogs.AssemblyCard.hide();
            }
          },
        },
      );
    },

    async submit(): Promise<boolean> {
      let confirmed = true;

      if (this.originalSellerStoreId !== this.localAssembly.sellerStore?.id) {
        confirmed = await this.$core.confirm(`Подтверждаете отгрузку со склада: ${this.localAssembly.sellerStoreString}?`);
      }

      if (confirmed === true) {
        if (this.localAssembly.isExisting === true) {
          return await AssembliesService.update(this.localAssembly);
        } else {
          return await AssembliesService.add(this.localAssembly);
        }
      } else {
        this.showRequisitesDialog();
        return false;
      }
    },

    validatePositionQty(value: Qty, position: AssemblyPosition) {
      return position.validatePositionQty(value, this.getPositionQtyReminder(position));
    },

    showOrderPositionRegistryDialog() {
      this.$core.dialogs.OrderPositionsRegistryDialog.showAsPromise({
        targetDocument: this.localAssembly,
        state: 'in-work',
        params: {
          // в сборку добираем позиции того же происхождения, что и у самой сборки
          isOfferBased: this.localAssembly.isOfferBased,
          seller: this.localAssembly.seller,
        }
      })
    },

    showImportAssemblyPositionDialog() {
      this.$core.dialogs.AssemblyPositionImportDialog.showAsPromise({
        assembly: this.localAssembly,
      })
    },

    getPositionQtyReminder(assemblyPosition) {
      if (assemblyPosition.id) {
        /** В случае редактирования количества в сборке, нужно знать максимальное возможное количество,
         * и оно лежит в shipmentStatus.assembled */
        return assemblyPosition.orderPosition.shipmentStatus.assembled;
      }
      return assemblyPosition.orderPosition.shipmentStatus.inWork;
    },

    /** f/5112 если позиция не отгружена или частично отгружена, то её можно редактировать */
    isPositionShippedEntirely(position) {
      return position.isShippedEntirely;
    },

    handleDeletePosition(position, index) {
      if (!this.isPositionShippedEntirely(position)) {
        this.localAssembly.deletePosition(null, index);
      }
    },

    getInWorkQty(position) {
      const reminder = this.getPositionQtyReminder(position);

      if (position.qty === null || position.qty.isEmpty()) {
        return reminder.format();
      }

      const left = reminder.minus(position.qty);

      return (left.lessThan(0) ? Qty.zero() : left).format();
    }
  },
});
</script>
